import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Check } from 'lucide-react';
import { getRecommendation, CONTACT_INFO } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';

import { Step0Package } from './start/Step0Package';
import { Step1ProjectDetails } from './start/Step1ProjectDetails';
import { Step5Contact } from './start/Step5Contact';
import { RecommendationScreen } from './start/RecommendationScreen';
import { LoadingOverlay } from './start/LoadingOverlay';
import { WizardProgressBar } from './start/WizardProgressBar';
import { WizardChecklistBanner } from './start/WizardChecklistBanner';
import { WizardConsentNotice } from './start/WizardConsentNotice';
import { WizardNavigationButtons } from './start/WizardNavigationButtons';
import { devWarn } from '../lib/devWarn';

import {
  STORAGE_KEY,
  TOTAL_STEPS,
  PACKAGE_TO_ROUTE,
  PACKAGE_DIAGNOSTIC_ANSWERS,
  VALID_PACKAGE_SLUGS,
  INITIAL_FORM_DATA,
  FILE_KEYS,
} from './start/wizardConfig';
import { validateStep, buildRecommendationForRoute } from './start/wizardValidation';

const StartIncorporation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const tr = (key) => t(language, key);

  const [currentStep, setCurrentStep] = useState(1);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submission, setSubmission] = useState(null); // { submission_id, calendly_url }
  const [submissionCompleted, setSubmissionCompleted] = useState(false);
  const [calendlyScheduled, setCalendlyScheduled] = useState(false);
  const [calendlyEventDateTime, setCalendlyEventDateTime] = useState(null);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [submitChecklist, setSubmitChecklist] = useState(null);

  const clearFieldError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  const errFor = (field) =>
    errors[field] ? (
      <p className="text-sm text-red-600 mt-1" data-testid={`err-${field}`}>{errors[field]}</p>
    ) : null;

  const updateFormData = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // When user picks a package, auto-fill ONLY the diagnostic answers the package clearly includes.
      // We never pre-fill "no" — those questions stay blank for the user to answer.
      if (field === 'selectedPackage' && value && PACKAGE_DIAGNOSTIC_ANSWERS[value]) {
        Object.assign(next, PACKAGE_DIAGNOSTIC_ANSWERS[value]);
      }
      // If user switches from "knows package" → "no", clear the selected package and any pre-fills.
      if (field === 'knowsPackage' && value === 'no') {
        next.selectedPackage = '';
      }
      return next;
    });
    if (errors[field]) clearFieldError(field);
  };

  const scrollTop = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  // ---- Hydrate from localStorage on mount ----
  // Intentional: these two effects run ONLY on mount; React Hooks linter rules
  // disabled because the closure capture is deliberate.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && saved.submissionCompleted && saved.submission) {
        if (saved.formData) setFormData((prev) => ({ ...prev, ...saved.formData }));
        if (saved.submission) setSubmission(saved.submission);
        if (saved.recommendation) setRecommendation(saved.recommendation);
        setSubmissionCompleted(true);
        setCalendlyScheduled(!!saved.calendlyScheduled);
        if (saved.calendlyEventDateTime) setCalendlyEventDateTime(saved.calendlyEventDateTime);
        setShowRecommendation(true);
      }
    } catch (e) {
      devWarn('[wizard] could not hydrate from localStorage:', e);
    }
  }, []);

  // ---- Pre-select package from URL query (?package=incorporate etc.) ----
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.submissionCompleted) return;
      }
    } catch (e) {
      devWarn('[wizard] could not read localStorage during URL preselect:', e);
    }

    const pkg = searchParams.get('package');
    if (!pkg || !VALID_PACKAGE_SLUGS.includes(pkg)) return;
    const diagnostic = PACKAGE_DIAGNOSTIC_ANSWERS[pkg] || {};
    setFormData((prev) => ({
      ...prev,
      knowsPackage: 'yes',
      selectedPackage: pkg,
      ...diagnostic,
    }));
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ---- Calendly: listen for scheduled-event postMessage (origin-filtered) ----
  useEffect(() => {
    const isCalendlyEvent = (e) =>
      e.origin === 'https://calendly.com' &&
      e.data && typeof e.data.event === 'string' &&
      e.data.event.indexOf('calendly.') === 0;

    const onMessage = (e) => {
      if (!isCalendlyEvent(e)) return;
      if (e.data.event === 'calendly.event_scheduled') {
        let dt = null;
        try {
          dt = e.data?.payload?.event?.start_time
            || e.data?.payload?.scheduled_event?.start_time
            || null;
        } catch (_err) { dt = null; }
        setCalendlyScheduled(true);
        if (dt) setCalendlyEventDateTime(dt);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // ---- Persist submission state on change ----
  useEffect(() => {
    if (!submissionCompleted) return;
    try {
      const payload = {
        submissionCompleted,
        submission,
        recommendation,
        calendlyScheduled,
        calendlyEventDateTime,
        formData: Object.fromEntries(
          Object.entries(formData).filter(([k]) => !FILE_KEYS.includes(k))
        ),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      devWarn('[wizard] could not persist to localStorage:', e);
    }
  }, [submissionCompleted, submission, recommendation, calendlyScheduled, calendlyEventDateTime, formData]);

  const sectionLabelForStep = (step) => {
    const titles = {
      1: tr('wizard.step0.title'),
      2: tr('wizard.simplified.step2.title'),
      3: tr('wizard.step5.title'),
    };
    return titles[step];
  };

  const handleNext = async () => {
    // Idempotent: never re-POST after success
    if (submissionCompleted) {
      setShowRecommendation(true);
      scrollTop();
      return;
    }

    const stepErrors = validateStep(currentStep, formData, tr);
    if (Object.keys(stepErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      return;
    }

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
      scrollTop();
      return;
    }

    // Final step — validate ALL steps before submission
    const allErrors = {};
    const checklistBySection = {};
    for (let s = 1; s <= TOTAL_STEPS; s++) {
      const stepErrs = validateStep(s, formData, tr);
      if (Object.keys(stepErrs).length > 0) {
        Object.assign(allErrors, stepErrs);
        checklistBySection[s] = Object.values(stepErrs);
      }
    }

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      const list = Object.entries(checklistBySection).map(([s, items]) => ({
        section: sectionLabelForStep(parseInt(s, 10)),
        items: Array.from(new Set(items)),
      }));
      setSubmitChecklist(list);
      const firstFailing = parseInt(Object.keys(checklistBySection)[0], 10);
      if (firstFailing && firstFailing !== currentStep) setCurrentStep(firstFailing);
      scrollTop();
      return;
    }

    // Submit
    setSubmitting(true);
    setSubmitError(null);
    setSubmitChecklist(null);
    try {
      const rec = getRecommendation(formData);
      const planName = rec.pricing && rec.pricing.type !== 'custom' ? rec.planName : 'Custom project';
      const fullPhone = `${formData.phoneCountryCode} ${formData.phone}`.trim();
      const answerPayload = { ...formData, recommendedPlan: planName, language, phone: fullPhone };

      const fd = new FormData();
      fd.append('wizard_data', JSON.stringify(answerPayload));

      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/submissions`, { method: 'POST', body: fd });
      if (!res.ok) {
        let message = `Request failed (${res.status})`;
        try {
          const body = await res.json();
          if (body && Array.isArray(body.detail?.missing)) {
            setSubmitChecklist([{ section: '', items: body.detail.missing }]);
            scrollTop();
            return;
          }
          if (typeof body.detail === 'string') message = body.detail;
          else if (Array.isArray(body.detail)) message = body.detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
          else if (body.detail) message = JSON.stringify(body.detail);
        } catch (parseErr) {
          devWarn('[wizard] non-JSON error body from /api/submissions:', parseErr);
        }
        throw new Error(message);
      }
      const data = await res.json();
      setSubmission(data);
      setRecommendation(rec);
      setSubmissionCompleted(true);
      setShowRecommendation(true);
      scrollTop();
    } catch (err) {
      setSubmitError(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      scrollTop();
    }
  };

  const handleRestart = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      devWarn('[wizard] could not clear localStorage on restart:', e);
    }
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setSubmitChecklist(null);
    setSubmission(null);
    setRecommendation(null);
    setSubmissionCompleted(false);
    setCalendlyScheduled(false);
    setCalendlyEventDateTime(null);
    setSubmitError(null);
    setShowRecommendation(false);
    setCurrentStep(1);
    scrollTop();
  };

  // ============== RECOMMENDATION + EMBEDDED CALENDLY + ALL-SET SCREEN ==============
  if (showRecommendation && recommendation) {
    const selectedSlug = formData.selectedPackage || (submission && submission.selectedPackage) || null;
    const selectedRoute = selectedSlug ? PACKAGE_TO_ROUTE[selectedSlug] : null;
    const computedRoute = recommendation.route;
    const hasConflict = !!(selectedRoute && computedRoute && selectedRoute !== computedRoute);

    const displayedRecommendation = selectedRoute
      ? buildRecommendationForRoute(selectedRoute)
      : recommendation;

    return (
      <RecommendationScreen
        recommendation={displayedRecommendation}
        userSelectedPackage={!!selectedRoute}
        hasConflict={hasConflict}
        calendlyScheduled={calendlyScheduled}
        calendlyEventDateTime={calendlyEventDateTime}
        calendlyUrl={(submission && submission.calendly_url) || CONTACT_INFO.calendly}
        prefillName={formData.fullName || ''}
        prefillEmail={formData.email || ''}
        language={language}
        tr={tr}
        onBackToWizard={() => {
          setShowRecommendation(false);
          scrollTop();
        }}
        onReturnHome={() => navigate('/')}
        onRestart={handleRestart}
      />
    );
  }

  const stepLabel = tr('wizard.stepOf')
    .replace('{{current}}', currentStep)
    .replace('{{total}}', TOTAL_STEPS);
  const progressPercentage = (currentStep / TOTAL_STEPS) * 100;

  const stepProps = { formData, updateFormData, errFor, tr };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{tr('wizard.heroTitle')}</h1>
          <p className="text-lg text-gray-600">{tr('wizard.heroSubtitle')}</p>
        </div>

        <WizardProgressBar
          stepLabel={stepLabel}
          progressPercentage={progressPercentage}
          completeSuffix={tr('wizard.complete')}
        />

        <WizardChecklistBanner checklist={submitChecklist} tr={tr} />

        {submissionCompleted && (
          <div className="mb-6 p-5 bg-blue-50 border border-blue-100 rounded-lg" data-testid="wizard-submitted-banner">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700 leading-relaxed">{tr('wizard.readonly.banner')}</p>
            </div>
          </div>
        )}

        <Card className="border-gray-200 shadow-lg">
          <CardContent className="p-6 md:p-8">
            <fieldset disabled={submissionCompleted} className={submissionCompleted ? 'opacity-70' : ''}>
              {currentStep === 1 && <Step0Package {...stepProps} />}
              {currentStep === 2 && <Step1ProjectDetails {...stepProps} />}
              {currentStep === 3 && <Step5Contact {...stepProps} />}
            </fieldset>

            {currentStep === 3 && !submissionCompleted && (
              <WizardConsentNotice language={language} />
            )}

            <WizardNavigationButtons
              currentStep={currentStep}
              totalSteps={TOTAL_STEPS}
              submitting={submitting}
              submissionCompleted={submissionCompleted}
              language={language}
              tr={tr}
              onBack={handleBack}
              onNext={handleNext}
            />

            {submitError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded" data-testid="wizard-error">
                {submitError}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {submitting && <LoadingOverlay tr={tr} hasUploads={false} />}
    </div>
  );
};

export default StartIncorporation;
